import { useState, useRef } from "react";
import { updateHomeConfig, uploadImage } from "../firebase/firebase-functions";
import "./homemanagement.css";

// ─────────────────────────────────────────────
// Small reusable image upload field
// ─────────────────────────────────────────────
const ImageUploadField = ({ label, hint, currentUrl, onUploaded, prefix }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      setError("Please choose a JPG, PNG, or WebP image.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Image must be under 10 MB.");
      return;
    }

    setError("");
    setUploading(true);
    setProgress(0);

    try {
      const url = await uploadImage(file, prefix, setProgress);
      onUploaded(url);
    } catch (err) {
      setError("Upload failed: " + err.message);
    } finally {
      setUploading(false);
      setProgress(0);
      // reset so re-selecting the same file fires onChange again
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="hm-image-field">
      <label className="hm-label">
        {label}
        {hint && <span className="hm-hint">{hint}</span>}
      </label>

      {currentUrl && (
        <div className="hm-preview-wrap">
          <img src={currentUrl} alt="current" className="hm-preview-img" />
        </div>
      )}

      <div className="hm-upload-row">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFile}
          disabled={uploading}
          className="hm-file-input"
        />
        {uploading && (
          <div className="hm-progress-wrap">
            <div
              className="hm-progress-bar"
              style={{ width: `${progress}%` }}
            />
            <span className="hm-progress-label">{progress}%</span>
          </div>
        )}
      </div>

      {error && <p className="hm-error">{error}</p>}
    </div>
  );
};

// ─────────────────────────────────────────────
// Main HomeManagement component
// Props:
//   homeConfig  – current config object (from App.jsx)
//   onSaved     – callback(newConfig) so App.jsx can update lifted state
// ─────────────────────────────────────────────
const HomeManagement = ({ homeConfig, onSaved }) => {
  const defaults = homeConfig || {};

  // Local form state — mirrors the homeConfig shape
  const [heroImageUrl, setHeroImageUrl] = useState(defaults.heroImageUrl || "");
  const [featuredTitle, setFeaturedTitle] = useState(
    defaults.featuredTitle || "",
  );
  const [featuredDescription, setFeaturedDescription] = useState(
    defaults.featuredDescription || "",
  );
  const [featuredSpotifyUrl, setFeaturedSpotifyUrl] = useState(
    defaults.featuredSpotifyUrl || "",
  );
  const [featuredImageUrl, setFeaturedImageUrl] = useState(
    defaults.featuredImageUrl || "",
  );
  const [featuredBgPositionY, setFeaturedBgPositionY] = useState(
    defaults.featuredBgPositionY ?? 0,
  );

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);

    const newConfig = {
      heroImageUrl,
      featuredTitle: featuredTitle.trim(),
      featuredDescription: featuredDescription.trim(),
      featuredSpotifyUrl: featuredSpotifyUrl.trim(),
      featuredImageUrl,
      featuredBgPositionY: Number(featuredBgPositionY),
    };

    const result = await updateHomeConfig(newConfig);

    if (result.success) {
      setSaveSuccess(true);
      if (onSaved) onSaved(newConfig);
      setTimeout(() => setSaveSuccess(false), 3000);
    } else {
      setSaveError(result.error || "Failed to save. Please try again.");
    }

    setSaving(false);
  };

  return (
    <div className="hm-root">
      {/* ── HERO SECTION ─────────────────────── */}
      <div className="hm-card">
        <h3 className="hm-card-title">🏠 Hero Section</h3>
        <p className="hm-card-desc">
          The main photo on the homepage. The short bio text is managed in the
          Bio Content section below.
        </p>

        <ImageUploadField
          label="Hero Photo"
          hint="Recommended: portrait orientation, at least 900 × 1100 px. JPG / PNG / WebP, max 10 MB."
          currentUrl={heroImageUrl}
          onUploaded={setHeroImageUrl}
          prefix="hero-photo"
        />
      </div>

      {/* ── FEATURED SONG SECTION ────────────── */}
      <div className="hm-card">
        <h3 className="hm-card-title">🎵 Featured Song Banner</h3>
        <p className="hm-card-desc">
          The full-width banner section beneath the hero. Use a wide / landscape
          image for the best look.
        </p>

        {/* Background image upload */}
        <ImageUploadField
          label="Banner Background Image"
          hint="Recommended: landscape, at least 1600 × 900 px. JPG / PNG / WebP, max 10 MB."
          currentUrl={featuredImageUrl}
          onUploaded={setFeaturedImageUrl}
          prefix="featured-bg"
        />

        {/* Y-position slider */}
        <div className="hm-field">
          <label className="hm-label">
            Image Vertical Position
            <span className="hm-hint">
              Drag to adjust which part of the image is centred vertically. 0% =
              top, 50% = centre, 100% = bottom.
            </span>
          </label>
          <div className="hm-slider-row">
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={featuredBgPositionY}
              onChange={(e) => setFeaturedBgPositionY(e.target.value)}
              className="hm-slider"
            />
            <span className="hm-slider-value">{featuredBgPositionY}%</span>
          </div>

          {/* Live preview */}
          {featuredImageUrl && (
            <div className="hm-banner-preview-wrap">
              <div
                className="hm-banner-preview"
                style={{
                  backgroundImage: `url("${featuredImageUrl}")`,
                  backgroundPositionY: `${featuredBgPositionY}%`,
                }}
              >
                <div className="hm-banner-overlay" />
                <p className="hm-banner-preview-label">Preview</p>
              </div>
            </div>
          )}
        </div>

        {/* Title */}
        <div className="hm-field">
          <label className="hm-label" htmlFor="feat-title">
            Song Title
          </label>
          <input
            id="feat-title"
            type="text"
            value={featuredTitle}
            onChange={(e) => setFeaturedTitle(e.target.value)}
            className="hm-input"
            placeholder="e.g. Girl on the Train"
            maxLength={100}
          />
        </div>

        {/* Description */}
        <div className="hm-field">
          <label className="hm-label" htmlFor="feat-desc">
            Description
          </label>
          <textarea
            id="feat-desc"
            value={featuredDescription}
            onChange={(e) => setFeaturedDescription(e.target.value)}
            className="hm-textarea"
            placeholder="A short line about the song…"
            rows={3}
            maxLength={300}
          />
          <p className="hm-char-count">{featuredDescription.length} / 300</p>
        </div>

        {/* Spotify link */}
        <div className="hm-field">
          <label className="hm-label" htmlFor="feat-spotify">
            Spotify Link
          </label>
          <input
            id="feat-spotify"
            type="url"
            value={featuredSpotifyUrl}
            onChange={(e) => setFeaturedSpotifyUrl(e.target.value)}
            className="hm-input"
            placeholder="https://open.spotify.com/track/…"
          />
        </div>
      </div>

      {/* ── SAVE BUTTON ──────────────────────── */}
      <div className="hm-footer">
        {saveError && <p className="hm-error">{saveError}</p>}
        {saveSuccess && (
          <p className="hm-success">✓ Changes saved successfully!</p>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary hm-save-btn"
        >
          {saving ? "Saving…" : "Save Home Page Settings"}
        </button>
      </div>
    </div>
  );
};

export default HomeManagement;
